import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Loader2 } from 'lucide-react';
import {
  MetricsRow, CompetencyChart, SubjectBreakdownPie,
  TopPerformersCard, AlertsCard, RegionCompetencyLineChart, DrillCard,
} from '../../../components/LevelDashboard';
import type { LevelMetrics, CompetencyBucket, TopPerformer, AlertItem } from '../../../types';
import type { SubjectBucket } from '../../../components/LevelDashboard';
import {
  useGetKvsResult9n11SummaryQuery,
  useGetKvsResult9n11DistributionQuery,
  useGetKvsResult9n11SubjectwiseQuery,
  useGetKvsResult9n11RegionwiseQuery,
  useGetKvsResult9n11TopPerformersQuery,
} from '../../../services/kvsResult9n11Api';
import { resultsSessionLabel } from '../../../services/kvsResultApi';
import { CLASSES, CLASS_NAMES } from '../data/index';
import PassPercentAnalytics from '../../../components/PassPercentAnalytics';
import { QuerySection, sectionState } from '../../../components/QuerySection';
import { StageScoreBandCard } from '../../../components/ScoreBandPanel';

const BASE = '/kvs-samagam/student-analytics/stages/secondary';
const levelSlug = (level: string) => level.toLowerCase().replace(/\s+/g, '-');

type ClassFilter = 'overall' | '9' | '11';

const HolisticDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [academicYear, setAcademicYear] = useState<number>(2026);
  const [classFilter, setClassFilter] = useState<ClassFilter>('overall');

  // Class 9/11 results carry no term dimension — always query "overall" (0).
  const term = 0;
  const querySuffix = `?year=${academicYear}`;

  // ========== RTK Query Queries ==========
  // Each section shows its own spinner / error (QuerySection), so a slow or failed
  // request no longer blanks the whole page or masquerades as "No data".
  const summaryQ = useGetKvsResult9n11SummaryQuery({ year: academicYear, term });
  const distQ = useGetKvsResult9n11DistributionQuery({ year: academicYear, term });
  const subjectQ = useGetKvsResult9n11SubjectwiseQuery({ year: academicYear, term });
  const regionQ = useGetKvsResult9n11RegionwiseQuery({ year: academicYear, term });
  const topQ = useGetKvsResult9n11TopPerformersQuery({ year: academicYear, term, order: 'desc' });
  const bottomQ = useGetKvsResult9n11TopPerformersQuery({ year: academicYear, term, order: 'asc' });
  const summaryRes = summaryQ.data;
  const distRes = distQ.data;
  const subjectRes = subjectQ.data;
  const regionRes = regionQ.data;
  const topRes = topQ.data;
  const bottomRes = bottomQ.data;
  const anyFetching = [summaryQ, distQ, subjectQ, regionQ, topQ, bottomQ].some(q => q.isFetching);

  // ========== Derived Data (Live) ==========
  const metrics: LevelMetrics = useMemo(() => {
    const d = summaryRes?.data;
    if (!d) return { totalStudents: 0, avgAttendance: 0, avgCompetency: 0, needsSupportCount: 0, excellentCount: 0, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0 };
    
    const target = classFilter === 'overall' ? d : d.byClass?.[classFilter];
    return {
      totalStudents: target?.totalStudents || 0,
      avgAttendance: 0,
      avgCompetency: target ? +(target.avgMarks || 0).toFixed(1) : 0,
      needsSupportCount: target?.needSupport || 0,
      excellentCount: target?.excellentStudents || 0,
      avgBehaviorScore: 0,
      avgPeerScore: 0,
      bmiNormalPercent: 0,
    };
  }, [summaryRes, classFilter]);

  const competencyBuckets: CompetencyBucket[] = useMemo(() => {
    const d = distRes?.data;
    if (!d) return [];
    const target = classFilter === 'overall' ? d.overall : d.byClass?.[classFilter];
    if (!target) return [];
    const total = target.total || 1;
    return [
      { level: 'Excellent', count: target.excellent, percent: +((target.excellent / total) * 100).toFixed(1) },
      { level: 'Good', count: target.good, percent: +((target.good / total) * 100).toFixed(1) },
      { level: 'Average', count: target.average, percent: +((target.average / total) * 100).toFixed(1) },
      { level: 'Below Average', count: target.belowAverage, percent: +((target.belowAverage / total) * 100).toFixed(1) },
    ];
  }, [distRes, classFilter]);

  const subjectData: SubjectBucket[] = useMemo(() => {
    const d = subjectRes?.data;
    if (!d) return [];
    const list = classFilter === 'overall' ? d.overall : d.byClass?.[classFilter];
    if (!list) return [];
    // The 9/11 source has no per-subject marks; the backend sends a single
    // "Overall" placeholder row. Hide the card until real subjects exist.
    return list
      .filter(item => item.subjectTitle !== 'Overall')
      .map(item => ({
        subject: item.subjectTitle,
        avgScore: +(item.avgWeightage || item.avgGrandTotal || 0).toFixed(1),
      }));
  }, [subjectRes, classFilter]);

  const regionCompetencyTrend = useMemo(() => {
    const d = regionRes?.data;
    if (!d) return [];
    const list = classFilter === 'overall' ? d.regions : d.byClass?.[classFilter];
    if (!list) return [];
    return list.map(item => ({
      regionId: item.roID,
      regionName: item.roName,
      avgCompetency: +(item.avgMarksPercentage || 0).toFixed(1),
      topSchoolName: item.topSchool?.schoolName || `School ${item.topSchool?.schoolID ?? ''}`,
      topSchoolScore: item.topSchool?.avgMarksPercentage ?? 0,
      worstSchoolName: item.bottomSchool?.schoolName || `School ${item.bottomSchool?.schoolID ?? ''}`,
      worstSchoolScore: item.bottomSchool?.avgMarksPercentage ?? 0,
    }));
  }, [regionRes, classFilter]);

  const topPerformers: TopPerformer[] = useMemo(() => {
    const d = topRes?.data;
    if (!d?.regions) return [];
    return d.regions.slice(0, 5).map((item, i) => ({
      id: `TP-${item.roID}-${i}`,
      name: `${item.roName} Region`,
      metric: 'Avg Marks',
      value: +(item.avgMarksPercentage || 0).toFixed(1),
    }));
  }, [topRes]);

  const alertItems: AlertItem[] = useMemo(() => {
    const d = bottomRes?.data;
    if (!d?.regions) return [];
    return d.regions.slice(0, 5).map((item, i) => ({
      id: `AL-${item.roID}-${i}`,
      name: `${item.roName} Region`,
      issue: `Avg ${(item.avgMarksPercentage || 0).toFixed(1)}% — ${(item.totalStudents || 0).toLocaleString()} students`,
      severity: item.avgMarksPercentage < 60 ? 'high' : item.avgMarksPercentage < 75 ? 'medium' : 'low',
    } as AlertItem));
  }, [bottomRes]);

  const regions = useMemo(() => {
    const d = regionRes?.data;
    if (!d) return [];
    const list = classFilter === 'overall' ? d.regions : d.byClass?.[classFilter];
    if (!list) return [];
    return list.map(r => ({
      regionId: r.roID,
      regionName: r.roName,
      metrics: {
        totalStudents: r.totalStudents,
        avgAttendance: 0,
        avgCompetency: +(r.avgMarksPercentage || 0).toFixed(1),
        needsSupportCount: 0,
        excellentCount: 0,
        avgBehaviorScore: 0,
        avgPeerScore: 0,
        bmiNormalPercent: 0,
      }
    }));
  }, [regionRes, classFilter]);

  // The subject pie sits beside the competency chart; with none to show, the KPI cards
  // take that space. isLoading (first load only), not isFetching, so a filter change —
  // which refetches but keeps the previous data — doesn't make the layout jump.
  const showSubjectPie = subjectData.length > 0 || subjectQ.isLoading || subjectQ.isError;

  const classLabel = classFilter === 'overall' ? 'Class IX & XI' : CLASS_NAMES[classFilter] || `Class ${classFilter}`;

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(new Set([2025, 2026, currentYear - 1, currentYear, currentYear + 1])).sort((a, b) => b - a);
  }, []);

  const handleChartClick = (level: string) => navigate(`${BASE}/list/regions/${levelSlug(level)}${querySuffix}`);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-3">
            Secondary Stage — Holistic Development
            {anyFetching && (
              <span role="status" className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
                <Loader2 size={12} className="animate-spin" /> Loading data…
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {classLabel} &mdash; Academic Year {resultsSessionLabel(academicYear)} &mdash; {metrics.totalStudents.toLocaleString()} students
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="classFilter" className="text-xs font-medium text-gray-600">Class</label>
            <select
              id="classFilter"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value as ClassFilter)}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall (9 & 11)</option>
              {(CLASSES as readonly string[]).map(c => (
                <option key={c} value={c}>{CLASS_NAMES[c] || `Class ${c}`}</option>
              ))}
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Year</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>{resultsSessionLabel(year)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Students by score band — the Secondary card on Stage Selection, built from the same summary. */}
      {metrics.totalStudents > 0 && (
        <StageScoreBandCard
          stats={{ totalStudents: metrics.totalStudents, excellentStudents: metrics.excellentCount, needSupport: metrics.needsSupportCount }}
          stage="secondary"
          matchesStageCard={classFilter === 'overall'}
        >
          <p>{metrics.totalStudents.toLocaleString()} students, each placed by their overall marks %.</p>
          <p className="text-[12px] text-gray-500">
            Excellent and Needs Support use the same cut-offs as Excellent and Below Average in the chart below; Moderate covers Good and Average.
          </p>
        </StageScoreBandCard>
      )}

      {/* KPI Row — beside the competency chart instead when there is no subject pie. */}
      {showSubjectPie && (
        <QuerySection state={sectionState(summaryQ)} label="summary">
          <MetricsRow m={metrics} />
        </QuerySection>
      )}

      {/* Pass Percentage Analytics — Classes IX & XI */}
      <PassPercentAnalytics
        year={academicYear}
        onRegionClick={(roID) => navigate(`${BASE}/region/${roID}${querySuffix}`)}
      />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuerySection state={sectionState(distQ)} label="distribution">
          <CompetencyChart buckets={competencyBuckets} onBarClick={handleChartClick} />
        </QuerySection>
        {/* Without per-subject marks there is no pie; the KPI cards fill its place. */}
        {showSubjectPie ? (
          <QuerySection state={sectionState(subjectQ)} label="subject-wise data" minHeight={320}>
            {subjectData.length > 0 && (
              <SubjectBreakdownPie data={subjectData} title="Subject-wise Average Weightage" />
            )}
          </QuerySection>
        ) : (
          <QuerySection state={sectionState(summaryQ)} label="summary" className="order-first lg:order-none">
            <MetricsRow m={metrics} layout="grid" />
          </QuerySection>
        )}
      </div>

      {(regionCompetencyTrend.length > 0 || regionQ.isFetching || regionQ.isError) && (
        <QuerySection state={sectionState(regionQ)} label="region trend" minHeight={320}>
          {regionCompetencyTrend.length > 0 && (
            <RegionCompetencyLineChart
              data={regionCompetencyTrend}
              onPointClick={(regionId) => navigate(`${BASE}/region/${regionId}${querySuffix}`)}
            />
          )}
        </QuerySection>
      )}

      {/* Top / Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuerySection state={sectionState(topQ)} label="top performers">
          <TopPerformersCard items={topPerformers} />
        </QuerySection>
        <QuerySection state={sectionState(bottomQ)} label="regions needing attention">
          <AlertsCard
            items={alertItems}
            onViewAll={() => navigate(`${BASE}/list/regions/below-average${querySuffix}`)}
            viewAllLabel="Need Attention Regions"
          />
        </QuerySection>
      </div>

      {/* Regions — Drill Down */}
      {(regionQ.isFetching || regionQ.isError) && regions.length === 0 && (
        <QuerySection state={sectionState(regionQ)} label="regions" minHeight={160} />
      )}
      {regions.length > 0 && (
        <QuerySection state={sectionState(regionQ)} label="regions">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Regions — Drill Down</h2>
            <button
              onClick={() => navigate(`${BASE}/list/regions${querySuffix}`)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition"
            >
              <List size={16} /> See All Regions
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {regions.map(r => (
              <DrillCard
                key={r.regionId}
                title={r.regionName}
                subtitle={`${r.metrics.totalStudents.toLocaleString()} students`}
                metrics={r.metrics}
                alerts={[]}
                navigateTo={`region/${r.regionId}${querySuffix}`}
              />
            ))}
          </div>
        </div>
        </QuerySection>
      )}
    </div>
  );
};

export default HolisticDashboard;
